import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Define supported languages
export const SUPPORTED_LANGUAGES = ['en', 'nl'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  getLocalizedPath: (path: string) => string;
  getUnlocalizedPath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
  initialLanguage?: SupportedLanguage;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  initialLanguage = DEFAULT_LANGUAGE 
}) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(initialLanguage);
  const location = useLocation();
  const navigate = useNavigate();

  // Extract language from URL path
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    if (SUPPORTED_LANGUAGES.includes(firstSegment as SupportedLanguage) && firstSegment !== DEFAULT_LANGUAGE) {
      setLanguageState(firstSegment as SupportedLanguage);
    } else {
      setLanguageState(DEFAULT_LANGUAGE);
    }
  }, [location.pathname]);

  // Change language and update URL
  const setLanguage = (lang: SupportedLanguage) => {
    if (lang === language) return;
    
    const unlocalizedPath = getUnlocalizedPath(location.pathname);
    const newPath = lang === DEFAULT_LANGUAGE ? unlocalizedPath : `/${lang}${unlocalizedPath}`;
    
    navigate(newPath + location.search + location.hash);
    setLanguageState(lang);
  };

  // Get localized path for a given path
  const getLocalizedPath = (path: string): string => {
    // If path already starts with language code, return as is
    const pathSegments = path.split('/').filter(Boolean);
    if (pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0] as SupportedLanguage)) {
      return path;
    }

    // Add language prefix for non-default languages
    return language === DEFAULT_LANGUAGE ? path : `/${language}${path.startsWith('/') ? path : `/${path}`}`;
  };

  // Get unlocalized path (remove language prefix)
  const getUnlocalizedPath = (path: string): string => {
    const pathSegments = path.split('/').filter(Boolean);
    
    if (pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0] as SupportedLanguage)) {
      return '/' + pathSegments.slice(1).join('/');
    }
    
    return path.startsWith('/') ? path : `/${path}`;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, getLocalizedPath, getUnlocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 